// ADDITIVE components for the Baltimore CFS / RMS Intelligence screen only:
// TypeInfoModal (public call-type intelligence) + AnalyticsPanel (WoW/MoM/YoY).
import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { X, BarChart3, Loader2, TrendingUp, TrendingDown, Minus, ExternalLink, MapPin, Trash2, Flame } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-[10px] font-mono text-white rounded-sm outline-none";

export function TypeInfoModal({ description, onClose }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setInfo(null); setErr("");
    client.get("/baltimore/type-info", { params: { description } })
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.status === 403 ? "Your role is not authorized for call-type intelligence." : "Could not load call-type information."));
  }, [description]);
  const maxM = Math.max(1, ...((info?.by_month || []).map(([, n]) => n)));
  return (
    <div className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center p-4" data-testid="type-info-modal" onClick={onClose}>
      <div className="bg-[#141518] border border-[#2C2D35] rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 sticky top-0 bg-[#141518]">
          <BarChart3 size={15} className="text-[#22D3EE]" />
          <div className="font-head font-bold text-sm text-white">{description}</div>
          <button data-testid="type-info-close" onClick={onClose} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={16} /></button>
        </div>
        {err && <div className="p-4 font-mono text-xs text-[#EF4444]">{err}</div>}
        {!info && !err && <div className="p-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#22D3EE]" /></div>}
        {info && (
          <div className="p-4 space-y-3">
            <div data-testid="type-info-public-banner" className="border border-[#22C55E]/50 bg-[#22C55E]/10 rounded px-2.5 py-1.5 font-mono text-[9px] text-[#4ade80]">
              PUBLIC INFORMATION — {info.classification}
            </div>
            <div className="font-mono text-[9px] text-[#9CA3AF]">
              SOURCE: <span className="text-white">{info.source.name}</span> · {info.source.publisher}
              <a href={info.source.url} target="_blank" rel="noreferrer" className="ml-1 text-[#22D3EE] inline-flex items-center gap-0.5">dataset <ExternalLink size={9} /></a>
              <div>FEED UPDATED: <span className="text-white">{info.feed_last_refresh ? new Date(info.feed_last_refresh).toLocaleString("en-GB", { hour12: false }) : "—"}</span></div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["TOTAL", info.total], ["FIRST SEEN", info.first_seen ? info.first_seen.slice(0, 10) : "—"],
                ["LAST SEEN", info.last_seen ? info.last_seen.slice(0, 10) : "—"],
                ["WoW 7d", info.wow.pct != null ? `${info.wow.pct > 0 ? "+" : ""}${info.wow.pct}%` : "—"]].map(([l, v]) => (
                <div key={l} className="border border-[#2C2D35] rounded p-1.5">
                  <div className="font-mono text-[8px] text-[#6B7280] uppercase">{l}</div>
                  <div className="font-mono text-[11px] font-bold text-white">{v}</div>
                </div>))}
            </div>
            <div className="font-mono text-[9px] text-[#9CA3AF]">
              Last 7d: <b className="text-white">{info.wow.current_7d}</b> vs prior 7d: <b className="text-white">{info.wow.previous_7d}</b>
            </div>
            {info.by_month.length > 0 && (
              <div data-testid="type-info-months">
                <div className="font-mono text-[9px] text-[#6B7280] uppercase mb-1">Monthly trend</div>
                {info.by_month.map(([m, n]) => (
                  <div key={m} className="flex items-center gap-2 py-0.5">
                    <span className="font-mono text-[9px] text-[#9CA3AF] w-14">{m}</span>
                    <div className="flex-1 h-2.5 bg-[#0d0e11] rounded-sm overflow-hidden">
                      <div className="h-full bg-[#22D3EE]" style={{ width: `${(100 * n) / maxM}%` }} />
                    </div>
                    <span className="font-mono text-[9px] text-white w-10 text-right">{n}</span>
                  </div>))}
              </div>)}
            <div className="grid grid-cols-2 gap-3">
              {[["Top districts", info.top_districts], ["Top neighborhoods", info.top_neighborhoods]].map(([l, rows]) => (
                <div key={l}>
                  <div className="font-mono text-[9px] text-[#6B7280] uppercase mb-1">{l}</div>
                  {rows.map(([d, n]) => (
                    <div key={d} className="flex justify-between font-mono text-[10px] py-0.5 border-b border-[#1E1F24]">
                      <span className="text-[#E5E7EB] truncate">{d}</span><span className="text-[#22D3EE]">{n}</span>
                    </div>))}
                </div>))}
            </div>
            <div className="font-mono text-[8px] text-[#6B7280]">
              Restricted CAD/RMS record details are not shown here — access them through CAD, RMS or Search All Calls under your existing permissions.
            </div>
          </div>)}
      </div>
    </div>
  );
}

const METRIC_LABEL = { wow: "WEEK / WEEK (7d)", mom: "MONTH / MONTH (30d)", yoy: "YEAR / YEAR (365d)" };

export function AnalyticsPanel({ districts, onDrillBaltimore }) {
  const [src, setSrc] = useState("baltimore");
  const [f, setF] = useState({ call_type: "", district: "", priority: "", status: "", location: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cadDrill, setCadDrill] = useState(null);
  const run = useCallback(async () => {
    setBusy(true);
    try {
      const params = { source: src };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await client.get("/baltimore/analytics", { params });
      setRes(data);
    } catch (e) { setRes({ error: e.response?.data?.detail || "Analytics unavailable" }); }
    setBusy(false);
  }, [src, f]);
  useEffect(() => { const t = setTimeout(run, 400); return () => clearTimeout(t); }, [run]);

  const drill = async (key) => {
    const m = res.metrics[key];
    const label = `${METRIC_LABEL[key]} — current window ${m.current_range[0]} → ${m.current_range[1]} (${m.current} calls)`;
    // Exact ISO boundaries so the drill-down list matches the card count 1:1.
    const [isoFrom, isoTo] = m.current_range_iso || m.current_range;
    if (src === "baltimore") {
      onDrillBaltimore({
        label, count: m.current,
        params: {
          date_from: isoFrom, date_to: isoTo,
          ...(f.call_type ? { description: f.call_type } : {}),
          ...(f.district ? { district: f.district } : {}),
          ...(f.priority ? { priority: f.priority } : {}),
          ...(f.location ? { q: f.location } : {}),
        },
      });
    } else {
      try {
        const { data } = await client.get("/calls/search", { params: {
          date_from: isoFrom, date_to: isoTo,
          ...(f.call_type ? { call_type: f.call_type } : {}),
          ...(f.status ? { status: f.status } : {}),
          ...(f.priority ? { priority: f.priority } : {}),
          ...(f.location ? { q: f.location } : {}), page_size: 50 } });
        setCadDrill({ label, rows: data.results, total: data.total });
      } catch { setCadDrill({ label, rows: [], total: 0 }); }
    }
  };

  return (
    <div className="border-b border-[#1E1F24]" data-testid="balt-analytics">
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="font-head text-[10px] font-bold text-[#9CA3AF] uppercase flex items-center gap-1.5"><BarChart3 size={12} /> Real-Time Analytics</span>
        <div className="flex rounded-sm overflow-hidden border border-[#2C2D35]">
          {[["baltimore", "BALTIMORE FEED"], ["cad", "AGENCY CAD"]].map(([k, l]) => (
            <button key={k} data-testid={`analytics-src-${k}`} onClick={() => setSrc(k)}
              className={`px-2 py-0.5 text-[9px] font-head font-bold ${src === k ? "bg-[#22D3EE] text-black" : "text-[#9CA3AF]"}`}>{l}</button>))}
        </div>
        {busy && <Loader2 size={11} className="animate-spin text-[#22D3EE]" />}
        {res?.last_refresh && (
          <span className="ml-auto font-mono text-[8px] text-[#6B7280]" data-testid="analytics-refresh-ts">
            DATA REFRESHED {new Date(res.last_refresh).toLocaleString("en-GB", { hour12: false })} · computed {new Date(res.computed_at).toLocaleTimeString("en-GB", { hour12: false })}
          </span>)}
      </div>
      <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
        <input data-testid="analytics-type" className={`${inp} w-36`} placeholder="Call type…" value={f.call_type}
          onChange={(e) => setF({ ...f, call_type: e.target.value })} />
        {src === "baltimore" ? (
          <select data-testid="analytics-district" className={inp} value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })}>
            <option value="">All districts</option>
            {districts.map(([d]) => <option key={d} value={d}>{d}</option>)}
          </select>
        ) : (
          <select data-testid="analytics-status" className={inp} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="">Any status</option>
            {["pending", "dispatched", "enroute", "active", "onscene", "delayed_onscene", "completed", "closed"].map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>)}
        <select data-testid="analytics-priority" className={inp} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
          <option value="">Any priority</option>
          {src === "baltimore"
            ? ["Emergency", "High"].map((p) => <option key={p} value={p}>{p}</option>)
            : [1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>P{p}</option>)}
        </select>
        <input data-testid="analytics-location" className={`${inp} w-32`} placeholder="Location…" value={f.location}
          onChange={(e) => setF({ ...f, location: e.target.value })} />
      </div>
      {res?.error && <div className="px-3 pb-2 font-mono text-[10px] text-[#EF4444]">{res.error}</div>}
      {res?.metrics && (
        <>
          <div className="px-3 pb-2 grid grid-cols-3 gap-2">
            {["wow", "mom", "yoy"].map((k) => {
              const m = res.metrics[k];
              const T = (m.pct || 0) > 0 ? TrendingUp : (m.pct || 0) < 0 ? TrendingDown : Minus;
              const col = (m.pct || 0) > 0 ? "#EF4444" : (m.pct || 0) < 0 ? "#22C55E" : "#9CA3AF";
              return (
                <button key={k} data-testid={`analytics-card-${k}`} onClick={() => drill(k)}
                  className="text-left border border-[#2C2D35] hover:border-[#22D3EE] rounded p-2 transition-colors">
                  <div className="font-mono text-[8px] text-[#6B7280] uppercase">{METRIC_LABEL[k]}</div>
                  <div className="font-mono text-lg font-bold text-white">{m.current}
                    <span className="text-[10px] text-[#6B7280] font-normal"> vs {m.previous}</span></div>
                  <div className="flex items-center gap-1 font-mono text-[10px] font-bold" style={{ color: col }}>
                    <T size={11} /> {m.delta > 0 ? "+" : ""}{m.delta} ({m.pct != null ? `${m.pct > 0 ? "+" : ""}${m.pct}%` : "no prior data"})
                  </div>
                  <div className="font-mono text-[7px] text-[#6B7280]">{m.current_range[0]} → {m.current_range[1]} · click for calls</div>
                </button>);
            })}
          </div>
          <div className="px-3 pb-2 font-mono text-[8px] text-[#6B7280]">{res.note} · {res.total_matching} total matching filter</div>
        </>)}
      {cadDrill && (
        <div className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center p-4" data-testid="cad-drill-modal" onClick={() => setCadDrill(null)}>
          <div className="bg-[#141518] border border-[#2C2D35] rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 sticky top-0 bg-[#141518]">
              <div className="font-head font-bold text-xs text-white">AGENCY CAD — {cadDrill.label}</div>
              <button data-testid="cad-drill-close" onClick={() => setCadDrill(null)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={16} /></button>
            </div>
            <div className="px-4 py-1.5 font-mono text-[9px] text-[#6B7280]">{cadDrill.total} call(s) counted · showing first {cadDrill.rows.length}</div>
            <table className="w-full text-left">
              <thead className="font-mono text-[9px] text-[#6B7280] uppercase">
                <tr><th className="px-4 py-1">CAD #</th><th className="px-2">Date</th><th className="px-2">Type</th><th className="px-2">Status</th><th className="px-2">Location</th></tr>
              </thead>
              <tbody>
                {cadDrill.rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#1E1F24] hover:bg-[#0d0e11] cursor-pointer" data-testid={`cad-drill-row-${r.cc_number}`}
                    onClick={() => window.open(`/incident/${r.id}`, "_blank")}>
                    <td className="px-4 py-1 font-mono text-[10px] text-[#22D3EE]">{r.cc_number}</td>
                    <td className="px-2 font-mono text-[9px] text-[#6B7280]">{r.created_at ? r.created_at.slice(0, 16).replace("T", " ") : "—"}</td>
                    <td className="px-2 text-[10px] text-white">{r.call_type_label}</td>
                    <td className="px-2 font-mono text-[9px] text-[#F59E0B] uppercase">{r.status}</td>
                    <td className="px-2 font-mono text-[9px] text-[#9CA3AF]">{r.address || r.location?.address || "—"}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>)}
    </div>
  );
}

export function HotspotWatch() {
  const [data, setData] = useState(null);
  const [hoods, setHoods] = useState([]);
  const [form, setForm] = useState({ neighborhood: "", threshold: "25" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    client.get("/baltimore/hotspots").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    client.get("/baltimore/neighborhoods").then((r) => setHoods(r.data.neighborhoods)).catch(() => {});
  }, [load]);
  const pin = async () => {
    if (!form.neighborhood) { toast.error("Pick a neighborhood to watch."); return; }
    setBusy(true);
    try {
      const { data: d } = await client.post("/baltimore/hotspots", { neighborhood: form.neighborhood, threshold: Number(form.threshold) || 25 });
      toast.success(`Watching ${d.watch.neighborhood} (+${d.watch.threshold}% WoW)${d.fired_now.length ? " — SPIKE ALERT fired now" : ""}`);
      setForm({ neighborhood: "", threshold: "25" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not pin watch"); }
    setBusy(false);
  };
  const unpin = async (w) => {
    try { await client.delete(`/baltimore/hotspots/${w.id}`); toast.success(`Stopped watching ${w.neighborhood}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove watch"); }
  };
  const checkNow = async () => {
    setBusy(true);
    try {
      const { data: d } = await client.post("/baltimore/hotspots/check");
      toast[d.fired_now.length ? "warning" : "success"](d.fired_now.length ? `${d.fired_now.length} hotspot alert(s) fired — command notified` : "No new spikes above thresholds");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    setBusy(false);
  };
  return (
    <div className="border-b border-[#1E1F24]" data-testid="hotspot-panel">
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="font-head text-[10px] font-bold text-[#9CA3AF] uppercase flex items-center gap-1.5"><Flame size={12} className="text-[#F97316]" /> Hotspot Watch</span>
        <select data-testid="hotspot-neighborhood" className={`${inp} max-w-[160px]`} value={form.neighborhood}
          onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}>
          <option value="">Pin a neighborhood…</option>
          {hoods.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className="font-mono text-[9px] text-[#6B7280]">alert at +</span>
        <input data-testid="hotspot-threshold" className={`${inp} w-12`} value={form.threshold}
          onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
        <span className="font-mono text-[9px] text-[#6B7280]">% WoW</span>
        <button data-testid="hotspot-pin" onClick={pin} disabled={busy}
          className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#F97316] text-black rounded-sm disabled:opacity-50">
          <MapPin size={10} /> PIN WATCH</button>
        <button data-testid="hotspot-check-now" onClick={checkNow} disabled={busy}
          className="px-2 py-1 text-[9px] font-head font-bold border border-[#2C2D35] text-[#9CA3AF] rounded-sm hover:text-white disabled:opacity-50">CHECK NOW</button>
      </div>
      {(data?.watches || []).length > 0 && (
        <div className="px-3 pb-1.5 space-y-1">
          {data.watches.map((w) => {
            const s = w.stats;
            const spiking = s && ((s.pct != null && s.pct >= w.threshold) || (s.previous_7d === 0 && s.current_7d >= 5));
            return (
              <div key={w.id} data-testid={`hotspot-row-${w.neighborhood}`}
                className={`flex items-center gap-2 font-mono text-[10px] border rounded-sm px-2 py-1 ${spiking ? "border-[#EF4444]/60 bg-[#EF4444]/10" : "border-[#2C2D35]"}`}>
                <MapPin size={10} className={spiking ? "text-[#EF4444]" : "text-[#F97316]"} />
                <span className="text-white font-bold">{w.neighborhood}</span>
                {s ? (
                  <span className="text-[#9CA3AF]">wk {s.current_7d} vs {s.previous_7d}
                    <b className="ml-1" style={{ color: (s.pct || 0) > 0 ? "#EF4444" : "#22C55E" }}>
                      {s.pct != null ? `${s.pct > 0 ? "+" : ""}${s.pct}%` : s.current_7d > 0 ? "NEW" : "—"}</b>
                    <span className="text-[#6B7280]"> · wk end {s.week_ending}</span></span>
                ) : <span className="text-[#6B7280]">no data</span>}
                {spiking && <span className="font-head font-bold text-[8px] text-[#EF4444]">SPIKING</span>}
                <span className="ml-auto text-[#6B7280]">+{w.threshold}% · {w.created_by}</span>
                <button data-testid={`hotspot-del-${w.neighborhood}`} onClick={() => unpin(w)} className="text-[#6B7280] hover:text-[#EF4444]"><Trash2 size={11} /></button>
              </div>);
          })}
        </div>)}
      {(data?.alerts || []).length > 0 && (
        <div data-testid="hotspot-alerts" className="px-3 pb-1.5 font-mono text-[9px] text-[#EF4444] space-y-0.5">
          {data.alerts.slice(0, 3).map((a) => (
            <div key={a.id}>🔥 {a.neighborhood} {a.pct != null ? `+${a.pct}%` : "new activity"} ({a.current_7d} vs {a.previous_7d}, wk {a.week}) — command notified {new Date(a.ts).toLocaleDateString("en-GB")}</div>))}
        </div>)}
    </div>
  );
}
